<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TaccAsanaAPI from 'dashboard/api/taccAsana';
import AsanaMark from './AsanaMark.vue';
import AsanaTaskModal from './AsanaTaskModal.vue';

const OPEN_ASANA_CREATE = 'TACC_OPEN_ASANA_CREATE';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const tasks = ref([]);
const activeTask = ref(null);
const isLoading = ref(false);
const errorMessage = ref('');
const modalRef = ref(null);

const hasTasks = computed(() => tasks.value.length > 0);
const statusLabel = task => (task?.completed ? 'Done' : 'In progress');

const formatDate = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
};

const loadTask = async () => {
  if (!props.conversationId) return;
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await TaccAsanaAPI.getLinkedTask(props.conversationId);
    tasks.value =
      response.data.tasks || (response.data.task ? [response.data.task] : []);
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      'Could not load Asana task.';
  } finally {
    isLoading.value = false;
  }
};

const openCreate = () => {
  activeTask.value = null;
  modalRef.value?.open('create');
};

const openManage = task => {
  if (!task) return;
  activeTask.value = task;
  modalRef.value?.open('manage', task);
};

const openAsana = task => {
  window.open(
    task?.permalink_url || 'https://app.asana.com/',
    '_blank',
    'noopener,noreferrer'
  );
};

const onSaved = nextTask => {
  const existingIndex = tasks.value.findIndex(task => task.id === nextTask.id);
  if (existingIndex >= 0) {
    tasks.value.splice(existingIndex, 1, nextTask);
  } else {
    tasks.value.unshift(nextTask);
  }
  activeTask.value = null;
  useAlert('Asana task saved.');
};

const onDeleted = deletedTask => {
  tasks.value = tasks.value.filter(task => task.id !== deletedTask?.id);
  activeTask.value = null;
  useAlert('Asana task deleted.');
};

const onOpenCreateEvent = conversationId => {
  if (String(conversationId) !== String(props.conversationId)) return;
  openCreate();
};

watch(
  () => props.conversationId,
  () => {
    tasks.value = [];
    activeTask.value = null;
    loadTask();
  }
);

onMounted(() => {
  emitter.on(OPEN_ASANA_CREATE, onOpenCreateEvent);
  loadTask();
});

onUnmounted(() => {
  emitter.off(OPEN_ASANA_CREATE, onOpenCreateEvent);
});
</script>

<template>
  <div class="px-3 py-3">
    <div v-if="isLoading" class="flex min-h-28 items-center justify-center">
      <Spinner />
    </div>

    <div
      v-else-if="errorMessage"
      class="flex flex-col items-center gap-3 py-5 text-center"
    >
      <p class="mb-0 text-sm text-n-ruby-10">{{ errorMessage }}</p>
      <Button xs outline color="slate" label="Retry" @click="loadTask" />
    </div>

    <div
      v-else-if="!hasTasks"
      class="flex flex-col items-center gap-2.5 py-5 text-center"
    >
      <AsanaMark />
      <p class="mb-0 text-sm font-medium text-n-slate-12">No task linked yet</p>
      <p class="mb-0 max-w-64 text-xs leading-5 text-n-slate-11">
        Create a task or subtask in Asana from this conversation.
      </p>
      <Button
        xs
        color="blue"
        icon="i-lucide-plus"
        label="Add task"
        @click="openCreate"
      />
    </div>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="task in tasks"
        :key="task.id"
        class="flex w-full items-start gap-2 rounded-lg bg-n-slate-2 p-3 transition hover:bg-n-slate-3"
      >
        <button
          type="button"
          class="flex min-w-0 flex-1 items-start gap-2 text-start"
          @click="openManage(task)"
        >
          <AsanaMark small :done="task.completed" class="mt-0.5" />
          <div class="min-w-0 flex-1">
            <p class="mb-1 truncate text-sm font-medium text-n-slate-12">
              {{ task.name }}
            </p>
            <div class="flex min-w-0 items-center gap-2">
              <span
                class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium"
                :class="
                  task.completed
                    ? 'bg-n-teal-3 text-n-teal-10'
                    : 'bg-n-blue-3 text-n-blue-10'
                "
              >
                {{ statusLabel(task) }}
              </span>
              <span
                v-if="task.due_on"
                class="truncate text-xs text-n-slate-11"
              >
                Due {{ formatDate(task.due_on) }}
              </span>
            </div>
          </div>
        </button>
        <button
          type="button"
          class="flex size-8 shrink-0 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
          title="Open in Asana"
          @click="openAsana(task)"
        >
          <span class="i-lucide-external-link text-sm text-n-slate-10" />
        </button>
      </div>

      <Button
        faded
        blue
        sm
        icon="i-lucide-plus"
        label="Add task"
        @click="openCreate"
      />
    </div>

    <AsanaTaskModal
      ref="modalRef"
      :conversation-id="conversationId"
      :task="activeTask"
      @saved="onSaved"
      @deleted="onDeleted"
    />
  </div>
</template>
